// ==============================================================================
// Validate a Custom Event script before submission
// Returns { errors: string[], warnings: string[] }
//
// TODO: This is duplicated in server/utils/analyzeCustomEventScript.js
//       Keep both in sync until a shared library is set up (see #33)
// ==============================================================================
const analyzeCustomEventScript = (script) => {
  const errors = [];
  const warnings = [];

  if (!script || !script.trim()) {
    errors.push('Script is empty.');
    return { errors, warnings };
  }

  // ------------------------------------------------------------------
  // Check 1: Valid JavaScript syntax (browser-native, no extra library)
  // ------------------------------------------------------------------
  try {
    // Safe: only parses for syntax errors, does not execute the script.
    // eslint-disable-next-line no-new-func
    new Function(script);
  } catch (e) {
    if (e instanceof SyntaxError) {
      errors.push(`Syntax error: ${e.message}`);
      return { errors, warnings }; // no point running further checks on broken code
    }
  }

  // ------------------------------------------------------------------
  // Strip comments before pattern checks so commented-out code is ignored
  // ------------------------------------------------------------------
  const stripped = script
    .replace(/\/\/[^\n]*/g, '')
    .replace(/\/\*[\s\S]*?\*\//g, '');

  // ------------------------------------------------------------------
  // Check 2: Must contain a real CUX_EVENT_API.reportEvent('name', ...) call
  // ------------------------------------------------------------------
  const validCuxCall = /CUX_EVENT_API\.reportEvent\s*\(\s*['"`][^'"`]+['"`]/.test(stripped);
  if (!validCuxCall) {
    errors.push('Script must contain at least one valid CUX_EVENT_API.reportEvent(\'event_name\', ...) call with a string event name.');
  }

  // ------------------------------------------------------------------
  // Check 3: No network / API calls
  // ------------------------------------------------------------------
  const networkPatterns = [
    { re: /fetch\s*\(/, label: 'fetch()' },
    { re: /XMLHttpRequest/, label: 'XMLHttpRequest' },
    { re: /\baxios\b/, label: 'axios' },
    { re: /\$\.(ajax|get|post|put|delete)\s*\(/, label: 'jQuery HTTP methods' },
    { re: /navigator\.sendBeacon\s*\(/, label: 'navigator.sendBeacon()' },
    { re: /\bnew\s+WebSocket\s*\(/, label: 'WebSocket' },
  ];
  networkPatterns.forEach(({ re, label }) => {
    if (re.test(stripped)) {
      errors.push(`Network requests are not allowed: "${label}" is forbidden.`);
    }
  });

  // ------------------------------------------------------------------
  // Check 4: No security-risky patterns
  // ------------------------------------------------------------------
  const securityPatterns = [
    { re: /\beval\s*\(/, label: 'eval()' },
    { re: /\bnew\s+Function\s*\(/, label: 'new Function()' },
    { re: /document\.cookie/, label: 'document.cookie' },
    { re: /\blocalStorage\b/, label: 'localStorage' },
    { re: /\bsessionStorage\b/, label: 'sessionStorage' },
    { re: /window\.location\s*=|location\.href\s*=|location\.replace\s*\(|location\.assign\s*\(/, label: 'page redirect (location)' },
    { re: /document\.write\s*\(/, label: 'document.write()' },
    { re: /\.innerHTML\s*=/, label: 'innerHTML assignment' },
    { re: /\.outerHTML\s*=/, label: 'outerHTML assignment' },
    { re: /\.insertAdjacentHTML\s*\(/, label: 'insertAdjacentHTML()' },
  ];
  securityPatterns.forEach(({ re, label }) => {
    if (re.test(stripped)) {
      errors.push(`Security risk: "${label}" is not permitted in custom event scripts.`);
    }
  });

  // ------------------------------------------------------------------
  // Check 5: No third-party dependencies or external URLs
  // ------------------------------------------------------------------
  const thirdPartyPatterns = [
    { re: /\bimport\s+/, label: 'import statement' },
    { re: /\brequire\s*\(/, label: 'require()' },
    { re: /<script/i, label: '<script> tag' },
    { re: /https?:\/\//, label: 'external URL (http/https)' },
    { re: /document\.createElement\s*\(\s*['"]script['"]/i, label: 'dynamic <script> element' },
  ];
  thirdPartyPatterns.forEach(({ re, label }) => {
    if (re.test(stripped)) {
      errors.push(`Third-party code is not allowed: "${label}" is forbidden.`);
    }
  });

  // ------------------------------------------------------------------
  // Check 6: Warnings (allowed but flagged)
  // ------------------------------------------------------------------
  if (/console\.(log|warn|error|info)\s*\(/.test(stripped)) {
    warnings.push('Remove console statements before deploying to production.');
  }
  if (/\bdebugger\b/.test(stripped)) {
    warnings.push('Remove "debugger" statements before deploying to production.');
  }
  if (/\b(setTimeout|setInterval)\s*\(/.test(stripped)) {
    warnings.push('Timers (setTimeout / setInterval) may cause memory leaks if not cleared properly.');
  }

  return { errors, warnings };
};

export default analyzeCustomEventScript;
